import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import api from "@/lib/api";

const POLL_MS = 8000;

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return "şimdi";
  if (sec < 3600) return `${Math.floor(sec / 60)} dk`;
  if (sec < 86400) return `${Math.floor(sec / 3600)} sa`;
  return `${Math.floor(sec / 86400)} g`;
}

export default function NotificationBell({ variant = "light" }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const openRef = useRef(false);
  openRef.current = open;

  const loadCount = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data.count || 0);
    } catch {
      /* ignore */
    }
  }, []);

  const loadList = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/notifications?limit=40");
      setItems(data || []);
    } catch {
      if (!silent) setItems([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    await loadCount();
    if (openRef.current) await loadList(true);
  }, [loadCount, loadList]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, POLL_MS);
    const onFocus = () => refresh();
    const onVis = () => { if (document.visibilityState === "visible") refresh(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("renka-notifications-refresh", refresh);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("renka-notifications-refresh", refresh);
    };
  }, [refresh]);

  useEffect(() => {
    if (open) {
      loadList();
      loadCount();
    }
  }, [open, loadList, loadCount]);

  const markRead = async (n) => {
    if (!n.read) {
      try {
        await api.post(`/notifications/${n.id}/read`);
        setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
        setUnread((c) => Math.max(0, c - 1));
      } catch {
        /* ignore */
      }
    }
    setOpen(false);
    if (n.link) {
      navigate(n.link);
      window.dispatchEvent(new CustomEvent("renka-parent-navigate", { detail: { link: n.link } }));
      window.dispatchEvent(new Event("renka-parent-reload"));
    }
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems((prev) => prev.map((x) => ({ ...x, read: true })));
      setUnread(0);
    } catch {
      /* ignore */
    }
  };

  const btnCls =
    variant === "dark"
      ? "relative rounded-lg p-2 text-slate-300 transition-colors hover:bg-slate-800 hover:text-white"
      : "relative rounded-lg p-2 text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="notification-bell" aria-label="Bildirimler" className={btnCls}>
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span
              data-testid="notification-badge"
              className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-violet-600 px-1 text-[10px] font-bold text-white"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(100vw-2rem,22rem)] rounded-2xl border-slate-200 p-0 shadow-xl" data-testid="notification-popover">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <p className="font-heading text-sm font-bold text-slate-900">Bildirimler</p>
          {unread > 0 && (
            <button
              type="button"
              onClick={markAll}
              data-testid="notification-read-all"
              className="inline-flex items-center gap-1 text-xs font-medium text-violet-600 hover:text-violet-700"
            >
              <CheckCheck className="h-3.5 w-3.5" /> Tümünü oku
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-violet-600" />
            </div>
          ) : items.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-slate-400">Bildirim yok.</p>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                type="button"
                data-testid={`notification-item-${n.id}`}
                onClick={() => markRead(n)}
                className={`flex w-full flex-col gap-0.5 border-b border-slate-50 px-4 py-3 text-left transition-colors hover:bg-slate-50 ${n.read ? "opacity-70" : "bg-violet-50/40"}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className={`text-sm ${n.read ? "font-medium text-slate-700" : "font-semibold text-slate-900"}`}>{n.title}</p>
                  {!n.read && <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-violet-600" />}
                </div>
                {n.body && <p className="line-clamp-2 text-xs text-slate-500">{n.body}</p>}
                <span className="text-[10px] text-slate-400">{timeAgo(n.created_at)}</span>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
